<template>
  <div class="ai-prompts-page">
    <section class="prompts-hero">
      <div class="label">
        <div>
          <span class="eyebrow">AI operations</span>
          <h4>AI Prompts</h4>
        </div>
        <button class="btn-refresh" :disabled="loading" @click="fetchAll">
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
      <p class="intro">
        View and edit the prompts used by scheduled AI jobs. Changes are stored as
        overrides and take effect on the next job run.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>
    <div v-if="apiMissing" class="notice warning">
      AI prompt endpoints are not available yet. The page is wired for the backend contract and
      will start working when <code>/api/admin/ai-prompts</code> is deployed.
    </div>

    <div class="kpi-row">
      <div class="kpi-card">
        <span class="kpi-label">Prompts</span>
        <span class="kpi-value">{{ prompts.length }}</span>
        <span class="kpi-sub">registered templates</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Edited</span>
        <span class="kpi-value">{{ editedCount }}</span>
        <span class="kpi-sub">overridden from code defaults</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Jobs</span>
        <span class="kpi-value">{{ connectedJobCount }}</span>
        <span class="kpi-sub">scheduled workflows connected</span>
      </div>
      <div class="kpi-card kpi-cost">
        <span class="kpi-label">30d cost</span>
        <span class="kpi-value">{{ fmtCost(totalPromptCost) }}</span>
        <span class="kpi-sub">from AI usage by prompt</span>
      </div>
    </div>

    <div class="prompt-tools">
      <input
        v-model.trim="search"
        class="prompt-search"
        type="search"
        placeholder="Search prompt, key, job, or description"
      >
      <select v-model="providerFilter" class="prompt-filter">
        <option value="all">All providers</option>
        <option
          v-for="provider in providerFilterOptions"
          :key="provider"
          :value="provider"
        >{{ provider }}</option>
      </select>
      <label class="edited-toggle">
        <input v-model="editedOnly" type="checkbox">
        <span>Edited only</span>
      </label>
    </div>

    <AdminTable
      :columns="promptColumns"
      :rows="filteredPrompts"
      :row-key="'key'"
      :loading="loading"
      :default-sort="{ key: 'usage', dir: 'desc' }"
      empty-text="No AI prompts match these filters."
      clickable
      @row-click="openPrompt"
    >
      <template v-slot:cell-name="{ row }">
        <div class="prompt-name-cell">
          <strong>{{ row.name || row.key }}</strong>
          <span>{{ row.description || 'No description provided yet.' }}</span>
        </div>
      </template>

      <template v-slot:cell-key="{ row }">
        <span class="code-pill">{{ row.key }}</span>
      </template>

      <template v-slot:cell-jobs="{ row }">
        <span class="job-chips">
          <span v-for="job in row.jobKeys" :key="job" class="job-chip">{{ job }}</span>
          <span v-if="!row.jobKeys.length" class="muted-sub">No jobs mapped</span>
        </span>
      </template>

      <template v-slot:cell-provider="{ row }">
        <span class="provider-cell">
          <span>{{ promptProvider(row) }}</span>
          <small>{{ promptModel(row) }}</small>
          <small class="mode-line" :class="{ on: promptWebSearch(row) }">
            {{ promptWebSearch(row) ? 'Hosted web search' : 'Standard prompt' }}
          </small>
        </span>
      </template>

      <template v-slot:cell-usage="{ row }">
        <span class="usage-cell">
          <strong>{{ fmtNum(usageFor(row.key).calls) }}</strong>
          <small>{{ fmtPct(usageFor(row.key).successRate) }} success</small>
          <small>{{ fmtCost(usageFor(row.key).costUsd) }}</small>
        </span>
      </template>

      <template v-slot:cell-status="{ row }">
        <span class="status-pill" :class="{ edited: row.isOverridden }">
          {{ row.isOverridden ? 'Edited' : 'Default' }}
        </span>
      </template>

      <template v-slot:cell-actions="{ row }">
        <button type="button" class="row-btn" @click.stop="openPrompt(row)">Edit</button>
      </template>
    </AdminTable>

    <div
      v-if="selectedPrompt"
      class="prompt-drawer"
      role="dialog"
      aria-modal="true"
      @click.self="closePrompt"
    >
      <aside class="prompt-panel">
        <header class="prompt-panel-head">
          <div>
            <span class="eyebrow">Prompt</span>
            <h5>{{ selectedPrompt.name || selectedPrompt.key }}</h5>
            <span class="prompt-key">{{ selectedPrompt.key }}</span>
          </div>
          <button type="button" class="panel-close" aria-label="Close" @click="closePrompt">x</button>
        </header>

        <div v-if="detailLoading" class="drawer-loading">Loading prompt...</div>

        <div v-else>
          <div class="drawer-message" v-if="saveMessage">{{ saveMessage }}</div>

          <section class="prompt-meta-grid">
            <div>
              <span>Description</span>
              <strong>{{ selectedPrompt.description || 'No description provided.' }}</strong>
            </div>
            <div>
              <span>Produces</span>
              <strong>{{ selectedPrompt.produces || 'No output documentation yet.' }}</strong>
            </div>
            <div>
              <span>Used by jobs</span>
              <strong>{{ selectedPrompt.jobKeys.length ? selectedPrompt.jobKeys.join(', ') : 'No jobs mapped' }}</strong>
            </div>
            <div>
              <span>30d usage</span>
              <strong>{{ fmtNum(usageFor(selectedPrompt.key).calls) }} calls / {{ fmtCost(usageFor(selectedPrompt.key).costUsd) }}</strong>
            </div>
            <div>
              <span>Default assignment</span>
              <strong>{{ promptDefaultProvider(selectedPrompt) }} / {{ promptDefaultModel(selectedPrompt) }}</strong>
            </div>
            <div>
              <span>Prompt mode</span>
              <strong>{{ draft.webSearchEnabled ? 'Hosted web search' : 'Standard prompt' }}</strong>
            </div>
          </section>

          <section class="prompt-edit-controls">
            <label>
              <span>Provider</span>
              <select v-model="draft.provider" :disabled="!providerChoices.length">
                <option value="">Default provider</option>
                <option
                  v-for="provider in providerChoices"
                  :key="provider"
                  :value="provider"
                >{{ provider }}</option>
              </select>
            </label>

            <label>
              <span>Model</span>
              <select v-model="draft.model" :disabled="!modelChoices.length">
                <option value="">Default model</option>
                <option
                  v-for="model in modelChoices"
                  :key="model"
                  :value="model"
                >{{ model }}</option>
              </select>
            </label>

            <label class="web-search-control">
              <span>Hosted web search</span>
              <button
                type="button"
                class="web-search-toggle"
                :class="{ enabled: draft.webSearchEnabled }"
                @click="draft.webSearchEnabled = !draft.webSearchEnabled"
              >
                <i></i>
                <strong>{{ draft.webSearchEnabled ? 'Enabled' : 'Disabled' }}</strong>
              </button>
            </label>

            <div class="modified-state" :class="{ modified: modifiedFromDefault }">
              {{ modifiedFromDefault ? 'Modified from default' : 'Matches code default' }}
            </div>
          </section>

          <div class="editor-head">
            <span>Effective prompt text</span>
            <button type="button" class="link-btn" @click="showDefault = !showDefault">
              {{ showDefault ? 'Hide default' : 'View default' }}
            </button>
          </div>
          <textarea
            ref="promptArea"
            v-model="draft.text"
            class="prompt-textarea"
            spellcheck="false"
            @input="autoGrow($event.target)"
          ></textarea>

          <div v-if="showDefault" class="default-block">
            <div class="editor-head">
              <span>Code default</span>
            </div>
            <textarea
              ref="defaultArea"
              :value="selectedPrompt.defaultText || ''"
              class="prompt-textarea readonly"
              readonly
              spellcheck="false"
            ></textarea>
          </div>

          <section class="usage-panel">
            <div>
              <span>Calls</span>
              <strong>{{ fmtNum(usageFor(selectedPrompt.key).calls) }}</strong>
            </div>
            <div>
              <span>Success</span>
              <strong>{{ fmtPct(usageFor(selectedPrompt.key).successRate) }}</strong>
            </div>
            <div>
              <span>Tokens</span>
              <strong>{{ fmtNum(usageFor(selectedPrompt.key).totalTokens) }}</strong>
            </div>
            <div>
              <span>Cost</span>
              <strong>{{ fmtCost(usageFor(selectedPrompt.key).costUsd) }}</strong>
            </div>
          </section>

          <footer class="prompt-panel-footer">
            <p>Changes affect future job runs only. Running jobs keep the prompt they already started with.</p>
            <div class="prompt-actions">
              <button
                type="button"
                class="row-btn reset"
                :disabled="resetting || !selectedPrompt.isOverridden"
                @click="resetPrompt"
              >Reset to default</button>
              <button
                type="button"
                class="btn-refresh"
                :disabled="saving || !hasPromptChanges"
                @click="savePrompt"
              >{{ saving ? 'Saving...' : 'Save changes' }}</button>
            </div>
          </footer>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import AdminTable from './shared/AdminTable.vue'

export default {
  name: 'AiPrompts',
  components: { AdminTable },
  data () {
    return {
      prompts: [],
      options: {
        providers: [],
        models: {}
      },
      usageByPrompt: {},
      loading: false,
      detailLoading: false,
      saving: false,
      resetting: false,
      error: '',
      notice: '',
      saveMessage: '',
      apiMissing: false,
      search: '',
      providerFilter: 'all',
      editedOnly: false,
      selectedPrompt: null,
      draft: {
        text: '',
        provider: '',
        model: '',
        webSearchEnabled: false
      },
      originalDraft: {
        text: '',
        provider: '',
        model: '',
        webSearchEnabled: false
      },
      showDefault: false
    }
  },
  computed: {
    promptColumns () {
      return [
        { key: 'name', label: 'Prompt', width: '24%', breakable: true, sortable: true, sortAccessor: (r) => (r.name || r.key || '') },
        { key: 'key', label: 'Key', width: '12%', breakable: true, sortable: true, sortAccessor: (r) => (r.key || '') },
        { key: 'jobs', label: 'Jobs', width: '19%', breakable: true },
        { key: 'provider', label: 'Provider / model', width: '17%', breakable: true, sortable: true, sortAccessor: (r) => this.providerSort(r) },
        { key: 'usage', label: '30d usage', width: '14%', breakable: true, sortable: true, sortAccessor: (r) => num(this.usageFor(r.key).costUsd) },
        { key: 'status', label: 'State', width: '7%', nowrap: true, sortable: true, sortAccessor: (r) => (r.isOverridden ? 1 : 0) },
        { key: 'actions', label: '', width: '7%', nowrap: true }
      ]
    },
    filteredPrompts () {
      const q = this.search.toLowerCase()
      return this.prompts.filter((prompt) => {
        if (this.editedOnly && !prompt.isOverridden) return false
        if (this.providerFilter !== 'all' && this.promptProvider(prompt) !== this.providerFilter) return false
        if (!q) return true
        return [
          prompt.key,
          prompt.name,
          prompt.description,
          prompt.produces,
          (prompt.jobKeys || []).join(' ')
        ].join(' ').toLowerCase().indexOf(q) !== -1
      })
    },
    editedCount () {
      return this.prompts.filter((prompt) => prompt.isOverridden).length
    },
    connectedJobCount () {
      const jobs = {}
      this.prompts.forEach((prompt) => {
        const jobKeys = prompt.jobKeys || []
        jobKeys.forEach((job) => { jobs[job] = true })
      })
      return Object.keys(jobs).length
    },
    totalPromptCost () {
      return Object.keys(this.usageByPrompt).reduce((sum, key) => {
        return sum + num(this.usageByPrompt[key].costUsd)
      }, 0)
    },
    providerFilterOptions () {
      const found = {}
      const providers = this.options.providers || []
        providers.forEach((provider) => { found[provider] = true })
        this.prompts.forEach((prompt) => {
        const provider = this.promptProvider(prompt)
        if (provider && provider !== 'default') found[provider] = true
      })
      return Object.keys(found).sort()
    },
    providerChoices () {
      const found = {}
      const providers = this.options.providers || []
      providers.forEach((provider) => { found[provider] = true })
      if (this.selectedPrompt && this.selectedPrompt.defaultProvider) found[this.selectedPrompt.defaultProvider] = true
      if (this.draft.provider) found[this.draft.provider] = true
      return Object.keys(found).sort()
    },
    modelChoices () {
      const models = this.options.models || {}
      const selectedProvider = this.draft.provider || (this.selectedPrompt && this.selectedPrompt.defaultProvider)
      const found = {}
      if (selectedProvider && Array.isArray(models[selectedProvider])) {
        models[selectedProvider].forEach((model) => { found[model] = true })
      }
      if (selectedProvider === 'openai') found['gpt-5'] = true
      if (this.selectedPrompt && this.selectedPrompt.defaultModel) found[this.selectedPrompt.defaultModel] = true
      if (this.draft.model) found[this.draft.model] = true
      return Object.keys(found).sort()
    },
    hasPromptChanges () {
      return this.draft.text !== this.originalDraft.text ||
        this.draft.provider !== this.originalDraft.provider ||
        this.draft.model !== this.originalDraft.model ||
        this.draft.webSearchEnabled !== this.originalDraft.webSearchEnabled
    },
    modifiedFromDefault () {
      if (!this.selectedPrompt) return false
      return String(this.draft.text || '') !== String(this.selectedPrompt.defaultText || '')
    }
  },
  watch: {
    'draft.provider' () {
      if (!this.draft.provider) return
      const choices = this.modelChoices
      if (this.draft.model && choices.indexOf(this.draft.model) === -1) {
        this.draft.model = ''
      }
    },
    '$route.query.prompt' () {
      this.openPromptFromRoute()
    },
    'draft.text' () {
      this.autoGrowAll()
    },
    showDefault () {
      this.autoGrowAll()
    }
  },
  mounted () {
    this.fetchAll()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    // Grow a textarea to fit its content so the prompt shows without an inner scrollbar.
    autoGrow (el) {
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight + 2}px`
    },
    autoGrowAll () {
      this.$nextTick(() => {
        this.autoGrow(this.$refs.promptArea)
        if (this.$refs.defaultArea) this.autoGrow(this.$refs.defaultArea)
      })
    },
    async fetchAll () {
      this.loading = true
      this.error = ''
      this.notice = ''
      this.apiMissing = false
      await Promise.all([this.fetchPrompts(), this.fetchOptions(), this.fetchUsage()])
      this.loading = false
      this.openPromptFromRoute()
    },
    async fetchPrompts () {
      try {
        const response = await axios.get(`${apiDomain}admin/ai-prompts`, { headers: this.authHeaders() })
        this.prompts = this.normalizePrompts(response.data && response.data.prompts)
      } catch (e) {
        if (this.isMissingEndpoint(e)) {
          this.apiMissing = true
          this.prompts = []
          return
        }
        this.error = this.errMsg(e)
        this.prompts = []
      }
    },
    async fetchOptions () {
      try {
        const response = await axios.get(`${apiDomain}admin/ai-prompts/options`, { headers: this.authHeaders() })
        const data = response.data || {}
        this.options = {
          providers: Array.isArray(data.providers) ? data.providers : [],
          models: data.models && typeof data.models === 'object' ? data.models : {}
        }
      } catch (e) {
        if (this.isMissingEndpoint(e)) return
        if (!this.error) this.error = this.errMsg(e)
      }
    },
    async fetchUsage () {
      try {
        const response = await axios.get(`${apiDomain}admin/ai-usage/summary?windowHours=720`, { headers: this.authHeaders() })
        this.usageByPrompt = this.normalizeUsage(response.data)
      } catch (e) {
        if (!this.error && !this.isMissingEndpoint(e)) this.error = this.errMsg(e)
        this.usageByPrompt = {}
      }
    },
    normalizePrompts (prompts) {
      if (!Array.isArray(prompts)) return []
      return prompts.map((prompt) => this.normalizePrompt(prompt))
    },
    normalizePrompt (prompt) {
      const item = prompt || {}
      const settings = item.settings && typeof item.settings === 'object' ? item.settings : {}
      const key = item.key || ''
      const fallbackProvider = {
        'amazon-product-search': 'openai',
        'part-release-date-research': 'openai',
        'field-resolve-deep': 'openai',
        'field-resolve-fast': 'deepseek'
      }[key] || ''
      const fallbackModel = {
        'amazon-product-search': 'gpt-5-mini',
        'part-release-date-research': 'gpt-5-mini',
        'field-resolve-deep': 'gpt-5',
        'field-resolve-fast': 'deepseek-v4-pro'
      }[key] || ''
      const defaultProvider = this.promptDefaultValue(item, 'defaultProvider', 'default_provider', fallbackProvider)
      const defaultModel = this.promptDefaultValue(item, 'defaultModel', 'default_model', fallbackModel)
      const defaultWebSearch = this.boolFrom(item.defaultWebSearchEnabled, item.default_web_search_enabled, key === 'part-release-date-research' || key === 'field-resolve-deep')
      const webSearchEnabled = this.boolFrom(item.webSearchEnabled, item.web_search_enabled, settings.webSearch, settings.web_search, defaultWebSearch)
      return {
        key,
        name: item.name || key || '',
        description: item.description || '',
        produces: item.produces || '',
        jobKeys: Array.isArray(item.jobKeys) ? item.jobKeys : (Array.isArray(item.job_keys) ? item.job_keys : []),
        provider: item.provider || '',
        model: item.model || '',
        defaultProvider,
        defaultModel,
        webSearchEnabled,
        defaultWebSearchEnabled: defaultWebSearch,
        settings,
        isOverridden: item.isOverridden !== undefined ? !!item.isOverridden : !!item.is_overridden,
        updatedAt: item.updatedAt || item.updated_at || '',
        text: item.text || '',
        defaultText: item.defaultText || item.default_text || ''
      }
    },
    normalizeUsage (data) {
      const source = data && data.summary ? data.summary : (data || {})
      const rows = Array.isArray(source.byPrompt) ? source.byPrompt : []
      return rows.reduce((acc, row) => {
        const key = row.key || row.promptKey || row.prompt_key || row.prompt || ''
        if (!key) return acc
        acc[key] = {
          calls: num(row.calls != null ? row.calls : (row.requestCount != null ? row.requestCount : row.count)),
          successRate: this.normalizeRate(row),
          totalTokens: num(row.totalTokens != null ? row.totalTokens : (row.total_tokens != null ? row.total_tokens : row.tokens)),
          costUsd: num(row.costUsd != null ? row.costUsd : (row.totalCostUsd != null ? row.totalCostUsd : (row.total_cost_usd != null ? row.total_cost_usd : row.cost)))
        }
        return acc
      }, {})
    },
    normalizeRate (row) {
      const rate = row.successRate != null ? row.successRate : row.success_rate
      if (rate != null && !isNaN(Number(rate))) {
        const n = Number(rate)
        return n > 1 ? n / 100 : n
      }
      const successes = num(row.successes)
      const failures = num(row.failures)
      const total = successes + failures
      return total ? successes / total : null
    },
    usageFor (key) {
      return this.usageByPrompt[key] || {
        calls: 0,
        successRate: null,
        totalTokens: 0,
        costUsd: 0
      }
    },
    promptDefaultValue (item, camelKey, snakeKey, fallback) {
      const direct = item && item[camelKey]
      const snake = item && item[snakeKey]
      return direct || snake || fallback || ''
    },
    boolFrom () {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i]
        if (value === undefined || value === null) continue
        if (typeof value === 'string') {
          const normalized = value.toLowerCase()
          if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true
          if (normalized === 'false' || normalized === '0' || normalized === 'no') return false
        }
        return !!value
      }
      return false
    },
    promptDefaultProvider (prompt) {
      return (prompt && prompt.defaultProvider) || 'default'
    },
    promptDefaultModel (prompt) {
      return (prompt && prompt.defaultModel) || 'default model'
    },
    promptProvider (prompt) {
      return (prompt && (prompt.provider || prompt.defaultProvider)) || 'default'
    },
    promptModel (prompt) {
      return (prompt && (prompt.model || prompt.defaultModel)) || 'default model'
    },
    promptWebSearch (prompt) {
      if (!prompt) return false
      if (prompt.webSearchEnabled !== undefined) return !!prompt.webSearchEnabled
      const settings = prompt.settings || {}
      if (settings.webSearch !== undefined) return !!settings.webSearch
      if (settings.web_search !== undefined) return !!settings.web_search
      return !!prompt.defaultWebSearchEnabled
    },
    providerSort (prompt) {
      return [this.promptProvider(prompt), this.promptModel(prompt), this.promptWebSearch(prompt) ? 'web' : 'standard'].join(':')
    },
    async openPrompt (row) {
      if (!row || !row.key) return
      if (this.selectedPrompt && this.selectedPrompt.key === row.key && !this.detailLoading) return
      this.selectedPrompt = this.normalizePrompt(row)
      this.prepareDraft(this.selectedPrompt)
      this.showDefault = false
      this.saveMessage = ''
      this.detailLoading = true
      try {
        const response = await axios.get(
          `${apiDomain}admin/ai-prompts/${encodeURIComponent(row.key)}`,
          { headers: this.authHeaders() }
        )
        const prompt = this.normalizePrompt(response.data && response.data.prompt)
        this.selectedPrompt = prompt
        this.prepareDraft(prompt)
      } catch (e) {
        if (this.isMissingEndpoint(e)) {
          this.apiMissing = true
          this.saveMessage = 'Prompt detail endpoint is not available yet.'
        } else {
          this.saveMessage = this.errMsg(e)
        }
      } finally {
        this.detailLoading = false
      }
    },
    openPromptFromRoute () {
      const key = this.$route && this.$route.query ? this.$route.query.prompt : ''
      if (!key || this.detailLoading || !this.prompts.length) return
      const match = this.prompts.find((prompt) => prompt.key === key)
      this.search = key
      if (match) this.openPrompt(match)
    },
    prepareDraft (prompt) {
      this.draft = {
        text: prompt.text || '',
        provider: prompt.provider || '',
        model: prompt.model || '',
        webSearchEnabled: !!prompt.webSearchEnabled
      }
      this.originalDraft = {
        text: this.draft.text,
        provider: this.draft.provider,
        model: this.draft.model,
        webSearchEnabled: this.draft.webSearchEnabled
      }
    },
    closePrompt () {
      if (this.hasPromptChanges) {
        const leave = window.confirm('Discard unsaved prompt changes?')
        if (!leave) return
      }
      this.selectedPrompt = null
      this.saveMessage = ''
    },
    async savePrompt () {
      if (!this.selectedPrompt || !this.hasPromptChanges) return
      this.saving = true
      this.saveMessage = ''
      try {
        const body = {
          text: this.draft.text,
          provider: this.draft.provider || null,
          model: this.draft.model || null,
          webSearchEnabled: !!this.draft.webSearchEnabled
        }
        const response = await axios.put(
          `${apiDomain}admin/ai-prompts/${encodeURIComponent(this.selectedPrompt.key)}`,
          body,
          { headers: this.authHeaders() }
        )
        const prompt = this.normalizePrompt(response.data && response.data.prompt)
        this.selectedPrompt = prompt
        this.prepareDraft(prompt)
        this.mergePrompt(prompt)
        this.saveMessage = 'Prompt saved. Changes apply on the next job run.'
      } catch (e) {
        this.saveMessage = this.errMsg(e)
      } finally {
        this.saving = false
      }
    },
    async resetPrompt () {
      if (!this.selectedPrompt) return
      const ok = window.confirm('Reset this prompt to the code default? This removes the saved override.')
      if (!ok) return
      this.resetting = true
      this.saveMessage = ''
      try {
        const response = await axios.post(
          `${apiDomain}admin/ai-prompts/${encodeURIComponent(this.selectedPrompt.key)}/reset`,
          {},
          { headers: this.authHeaders() }
        )
        const prompt = this.normalizePrompt(response.data && response.data.prompt)
        this.selectedPrompt = prompt
        this.prepareDraft(prompt)
        this.mergePrompt(prompt)
        this.saveMessage = 'Prompt reset to the code default.'
      } catch (e) {
        this.saveMessage = this.errMsg(e)
      } finally {
        this.resetting = false
      }
    },
    mergePrompt (prompt) {
      const index = this.prompts.findIndex((item) => item.key === prompt.key)
      if (index === -1) {
        this.prompts.push(prompt)
      } else {
        this.$set(this.prompts, index, Object.assign({}, this.prompts[index], prompt))
      }
    },
    isMissingEndpoint (e) {
      const status = e && e.response && e.response.status
      return status === 404 || status === 405 || status === 501
    },
    fmtCost (value) {
      const n = Number(value)
      if (isNaN(n)) return '$0.00'
      if (n === 0) return '$0.00'
      return n < 0.01 ? '$' + n.toFixed(6) : '$' + n.toFixed(2)
    },
    fmtNum (value) {
      const n = Number(value)
      if (isNaN(n)) return '0'
      return n.toLocaleString('en-US')
    },
    fmtPct (rate) {
      if (rate === null || rate === undefined || rate === '') return '-'
      const n = Number(rate)
      if (isNaN(n)) return '-'
      return (n * 100).toFixed(1) + '%'
    },
    errMsg (e) {
      if (e && e.response && e.response.status === 403) {
        return 'Admin access required. You do not have permission to manage AI prompts.'
      }
      const data = e && e.response && e.response.data ? e.response.data : null
      if (data && data.error && data.error.message) return data.error.message
      if (data && data.message) return data.message
      if (data && data.error && typeof data.error === 'string') return data.error
      return (e && e.message) || 'Request failed'
    }
  }
}

function num (value) {
  const n = Number(value)
  return isNaN(n) ? 0 : n
}
</script>

<style scoped>
.ai-prompts-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}


.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 800px;
  margin: 0.6rem 0 1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.btn-refresh,
.row-btn,
.link-btn {
  border: 1px solid rgba(32, 184, 235, 0.78);
  border-radius: 6px;
  background: rgba(5, 14, 22, 0.72);
  color: #20c8f8;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.btn-refresh {
  min-width: 100px;
  padding: 10px 18px;
  margin-bottom: 6px;
}

.row-btn,
.link-btn {
  padding: 8px 12px;
}

.link-btn {
  border-color: rgba(67, 72, 87, 0.9);
  color: #aeb8ca;
}

.btn-refresh:hover,
.row-btn:hover,
.link-btn:hover {
  border-color: #2bd0ff;
  background: rgba(32, 184, 235, 0.12);
  color: #f2f8fb;
}

.btn-refresh:disabled,
.row-btn:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.row-btn.reset {
  border-color: rgba(255, 96, 128, 0.65);
  color: #ff7893;
}

.alert,
.notice {
  margin: 1rem 0;
  padding: 12px 14px;
  border: 1px solid rgba(32, 184, 235, 0.54);
  border-radius: 7px;
  background: rgba(6, 39, 53, 0.5);
  color: #b8e9f8;
  font-size: 13px;
}

.alert,
.notice.warning {
  border-color: rgba(229, 198, 92, 0.58);
  background: rgba(58, 47, 18, 0.42);
  color: #eed77e;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 18px 0;
}

.kpi-card {
  display: grid;
  gap: 6px;
  min-height: 88px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background: rgba(9, 14, 22, 0.62);
}

.kpi-label,
.kpi-sub {
  color: #8c96aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.kpi-sub {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.kpi-value {
  color: #dce2ee;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.kpi-cost .kpi-value {
  color: #e2da32;
}

.prompt-tools {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px auto;
  gap: 10px;
  margin: 0 0 12px;
}

.prompt-search,
.prompt-filter,
.prompt-edit-controls select {
  min-height: 38px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  background: rgba(5, 8, 14, 0.78);
  color: #dce2ee;
  font-size: 13px;
}

.prompt-search {
  padding: 0 12px;
}

.prompt-filter,
.prompt-edit-controls select {
  padding: 0 10px;
}

.edited-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  color: #a4acbc;
  font-size: 13px;
  font-weight: 700;
}

.prompt-name-cell {
  display: grid;
  gap: 5px;
}

.prompt-name-cell strong {
  color: #dbe2ee;
  font-size: 14px;
}

.prompt-name-cell span,
.muted-sub {
  color: #8f98aa;
  font-size: 12px;
}

.code-pill,
.job-chip,
.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 3px 8px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 999px;
  background: rgba(18, 23, 34, 0.7);
  color: #aeb8ca;
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
}

.code-pill {
  border-radius: 5px;
  font-family: Consolas, 'Courier New', monospace;
}

.job-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.job-chip {
  border-color: rgba(25, 151, 198, 0.42);
  color: #86d9f2;
}

.status-pill.edited {
  border-color: rgba(229, 198, 92, 0.58);
  background: rgba(58, 47, 18, 0.42);
  color: #eed77e;
}

.provider-cell,
.usage-cell {
  display: grid;
  gap: 4px;
}

.provider-cell small,
.usage-cell small {
  color: #8f98aa;
  font-size: 12px;
}

.provider-cell .mode-line {
  color: #7f8798;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.provider-cell .mode-line.on {
  color: #60d7f8;
}

.usage-cell strong {
  color: #dce2ee;
}

.prompt-drawer {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
  background: rgba(2, 5, 9, 0.64);
  backdrop-filter: blur(2px);
}

.prompt-panel {
  width: min(760px, 94vw);
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  border-left: 1px solid rgba(67, 72, 87, 0.9);
  background: rgba(6, 10, 16, 0.96);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.45);
}

.prompt-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.75);
}

.prompt-panel-head h5 {
  margin: 0;
  color: #e2e7f0;
  font-size: 23px;
  font-weight: 800;
}

.prompt-key {
  display: inline-block;
  margin-top: 6px;
  color: #20c8f8;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
}

.panel-close {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(7, 12, 18, 0.86);
  color: #aeb8ca;
  cursor: pointer;
  font-size: 17px;
  font-weight: 800;
}

.drawer-loading,
.drawer-message {
  margin: 16px 0;
  padding: 12px;
  border: 1px solid rgba(32, 184, 235, 0.5);
  border-radius: 7px;
  background: rgba(6, 39, 53, 0.42);
  color: #b8e9f8;
}

.prompt-meta-grid,
.usage-panel {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 18px 0;
}

.prompt-meta-grid > div,
.usage-panel > div {
  display: grid;
  gap: 7px;
  padding: 13px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 7px;
  background: rgba(10, 15, 23, 0.7);
}

.prompt-meta-grid span,
.usage-panel span,
.prompt-edit-controls span,
.editor-head {
  color: #8c96aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.prompt-meta-grid strong,
.usage-panel strong {
  color: #dce2ee;
  font-size: 13px;
  line-height: 1.45;
}

.prompt-edit-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(180px, 0.8fr);
  gap: 10px;
  align-items: end;
  margin: 18px 0;
}

.prompt-edit-controls label {
  display: grid;
  gap: 7px;
}

.modified-state {
  grid-column: 1 / -1;
  justify-self: end;
  min-height: 38px;
  padding: 11px 12px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 7px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.modified-state.modified {
  border-color: rgba(229, 198, 92, 0.58);
  color: #eed77e;
}

.web-search-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 38px;
  padding: 6px 11px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  background: rgba(5, 8, 14, 0.78);
  color: #8f98aa;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.web-search-toggle i {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 999px;
  background: rgba(16, 22, 32, 0.9);
}

.web-search-toggle i::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #8c96aa;
  transition: transform 180ms ease, background 180ms ease;
}

.web-search-toggle.enabled {
  border-color: rgba(32, 184, 235, 0.72);
  color: #7ddffc;
}

.web-search-toggle.enabled i {
  border-color: rgba(32, 184, 235, 0.7);
  background: rgba(4, 68, 90, 0.45);
}

.web-search-toggle.enabled i::after {
  transform: translateX(16px);
  background: #20c8f8;
}

.editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 8px;
}

.prompt-textarea {
  width: 100%;
  min-height: 390px;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background: rgba(3, 6, 10, 0.88);
  color: #dce2ee;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  overflow-y: hidden;
}

.prompt-textarea:focus {
  border-color: rgba(32, 184, 235, 0.8);
  outline: none;
}

.prompt-textarea.readonly {
  min-height: 240px;
  color: #9aa6ba;
}

.prompt-panel-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  gap: 18px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(67, 72, 87, 0.75);
}

.prompt-panel-footer p {
  min-width: 0;
  max-width: 440px;
  margin: 0;
  color: #8f98aa;
  font-size: 13px;
  line-height: 1.45;
}

.prompt-panel-footer .prompt-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 10px;
}

.prompt-panel-footer .prompt-actions > button {
  flex: 0 0 auto;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .kpi-row,
  .prompt-meta-grid,
  .usage-panel,
  .prompt-edit-controls,
  .prompt-tools {
    grid-template-columns: 1fr;
  }

  .prompt-panel-footer {
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
  }

  .prompt-panel-footer p {
    max-width: none;
  }

  .prompt-panel-footer .prompt-actions {
    justify-content: flex-end;
  }

  .modified-state {
    justify-self: stretch;
  }
}
</style>
